import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, ChevronLeft, ChevronRight } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import StatusBadge from "../components/StatusBadge";
import { PageLoading, EmptyState, ErrorState } from "../components/States";
import { api } from "../lib/api";
import { fmtDateTime, fmtMoney, setPageTitle } from "../lib/format";

export default function HostInvoices() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);

  const load = (p = page) => {
    setError(null);
    api.hostInvoices(p).then(setData).catch((e) => { setError(e.message); setData({ items: [], total: 0, page_size: 25 }); });
  };
  useEffect(() => { setPageTitle("Host Invoices"); load(1); }, []); // eslint-disable-line

  const rows = data?.items || [];
  const pages = Math.max(1, Math.ceil((data?.total || 0) / (data?.page_size || 25)));

  return (
    <div className="min-h-screen flex flex-col" data-testid="host-invoices-page">
      <Navbar />
      <main className="flex-1 max-w-4xl w-full mx-auto px-5 sm:px-8 pt-28 pb-16">
        <p className="section-label">Host</p>
        <h1 className="type-display mt-3">Statements</h1>
        <p className="mt-1 text-sm text-zinc-400">Your earnings per booking — your listed parking price minus the applicable MyRedPark host service fee. The MyRedPark service fee is charged to the driver; payment processing is a separate amount.</p>
        <div className="mt-8">
          {error ? <ErrorState body={error} onRetry={() => load()} /> : data === null ? <PageLoading label="Loading statements…" /> :
            rows.length === 0 ? (
              <EmptyState icon={<FileText className="w-7 h-7 text-[#FF3B45]" />} title="No statements yet"
                body="Statements appear here once a guest's payment completes."
                action={<Link to="/host/listings/new" data-testid="host-invoices-new" className="btn-primary text-sm">Create a listing</Link>} />
            ) : (
              <div className="space-y-4" data-testid="host-invoices-list">
                {rows.map((inv) => (
                  <div key={inv.invoice_id} className="card-dark p-5" data-testid={`host-invoice-${inv.invoice_id}`}>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <p className="font-bold">{inv.spot_title}</p>
                        <p className="text-xs text-zinc-500 mt-1 font-mono">{inv.invoice_no} · {inv.booking_id}</p>
                        <p className="text-xs text-zinc-500">{fmtDateTime(inv.issued_at)} · {inv.pricing_model} pricing</p>
                      </div>
                      <div className="text-right">
                        <p className="font-display font-bold text-lg text-emerald-400">{fmtMoney((inv.host_payout_cents || 0) / 100, (inv.currency || "usd").toUpperCase())}</p>
                        <StatusBadge value={inv.status === "paid" ? "paid" : inv.status} />
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-[#1F2328] space-y-1.5 text-sm">
                      <div className="flex justify-between text-zinc-400"><span>Listed parking value</span><span>{fmtMoney((inv.parking_cents || 0) / 100, (inv.currency || "usd").toUpperCase())}</span></div>
                      {(inv.extension_lines || []).map((l, i) => (
                        <div key={i} className="flex justify-between text-zinc-400"><span>{l.label}</span><span>{fmtMoney((l.amount_cents || 0) / 100, (inv.currency || "usd").toUpperCase())}</span></div>
                      ))}
                      <div className="flex justify-between font-semibold text-white pt-1.5"><span>Your payout</span><span>{fmtMoney((inv.host_payout_cents || 0) / 100, (inv.currency || "usd").toUpperCase())}</span></div>
                      <p className="text-[11px] text-zinc-600 leading-relaxed pt-1">{inv.service_fee_note}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          {data && pages > 1 && (
            <div className="mt-5 flex items-center justify-between" data-testid="host-invoices-pager">
              <p className="text-xs text-zinc-500">Page {page} of {pages}</p>
              <div className="flex gap-2">
                <button disabled={page <= 1} onClick={() => { setPage(page - 1); load(page - 1); }} data-testid="host-invoices-prev" className="btn-secondary !px-3 !py-1.5 text-xs disabled:opacity-40"><ChevronLeft className="w-3.5 h-3.5" /></button>
                <button disabled={page >= pages} onClick={() => { setPage(page + 1); load(page + 1); }} data-testid="host-invoices-next" className="btn-secondary !px-3 !py-1.5 text-xs disabled:opacity-40"><ChevronRight className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
